import type {
  BucketAmounts,
  CallAgent,
  HoldOutcome,
  LedgerReportRange,
  PlayerBucket,
  ReleaseReason,
  SupportAdjustmentReason,
  SystemAccountKind,
} from "@pax/api-contract/accounting/v1alpha1";
import { readableCode } from "@/features/credits/journals";

/** An agent version as the wallet names it, or undefined when the hold has none. */
export const agentLabel = (agent: CallAgent | null | undefined) =>
  agent ? `${agent.name} v${agent.version}` : undefined;

export const outcomeLabels: Record<HoldOutcome | ReleaseReason, string> = {
  settled: "Settled",
  released: "Released",
  open: "Open",
  not_sent: "Not sent",
  abandoned: "Abandoned",
};

export const bucketLabels: Record<PlayerBucket, string> = {
  daily: "Daily",
  moderation: "Moderation",
  signup: "Signup",
  sponsorship: "Sponsorship",
  commission: "Commission",
  compensation: "Compensation",
  subscription: "Subscription",
  oneTimePurchase: "One-time purchase",
};

export const supportReasonLabels: Record<SupportAdjustmentReason, string> = {
  competition_winner: "Competition winner",
  refund: "Refund",
  creator_sponsorship: "Creator sponsorship",
  deduction: "Deduction",
  other: "Other",
};

export const accountKindLabels: Record<SystemAccountKind, string> = {
  granted: "Granted",
  used: "Used",
  expired: "Expired",
  removed: "Removed",
  pending: "Pending",
};

/**
 * Journals by the kind of system account they post against. The ledger has no journal kinds of its
 * own; debt repayment is the one journal that only moves credits between a player's balances. The
 * direction follows that account: a granted account or pending deposit pays players, a used,
 * expired, or removed account takes from them, and reward accrual and forfeits never touch them.
 */
export interface JournalKind {
  key: string;
  label: string;
  /** Which way the kind moves player balances: in (1), out (-1), or neither (0). */
  direction: 1 | -1 | 0;
  journals: readonly string[];
}

export const journalKinds: readonly JournalKind[] = [
  {
    key: "granted",
    label: "Granted",
    direction: 1,
    journals: [
      "compensation_grant",
      "daily_grant",
      "moderation_grant",
      "opening_balance",
      "other_reward",
      "purchased_credits",
      "signup_grant",
      "sponsorship_grant",
      "subscription_grant",
      "support_grant",
    ],
  },
  { key: "used", label: "Used", direction: -1, journals: ["ai_settlement"] },
  {
    key: "creatorRewards",
    label: "Rewards set aside",
    direction: 0,
    journals: ["pending_creator_reward", "creator_reward_forfeit"],
  },
  {
    key: "rewardDeposits",
    label: "Reward deposits",
    direction: 1,
    journals: ["creator_reward_deposit"],
  },
  {
    key: "expired",
    label: "Expired",
    direction: -1,
    journals: ["daily_expiration", "moderation_expiration"],
  },
  {
    key: "removed",
    label: "Removed",
    direction: -1,
    journals: [
      "chargeback_removal",
      "closure_removal",
      "failed_payment_removal",
      "refund_removal",
      "support_removal",
    ],
  },
  { key: "betweenBalances", label: "Between balances", direction: 0, journals: ["debt_repayment"] },
];

/**
 * One name for each journal and the system account it posts against, so System flows and Journals
 * call them the same: `purchased_credits` and `purchased_credits_granted` are both "Purchases".
 */
const reportNames: readonly { journal?: string; account?: string; label: string }[] = [
  { journal: "purchased_credits", account: "purchased_credits_granted", label: "Purchases" },
  {
    journal: "subscription_grant",
    account: "subscription_credits_granted",
    label: "Subscriptions",
  },
  { journal: "signup_grant", account: "signup_credits_granted", label: "Signup bonuses" },
  { journal: "daily_grant", account: "daily_credits_granted", label: "Daily credits" },
  {
    journal: "moderation_grant",
    account: "moderation_credits_granted",
    label: "Moderation credits",
  },
  { journal: "sponsorship_grant", account: "sponsorship_credits_granted", label: "Sponsorships" },
  { journal: "compensation_grant", account: "compensation_granted", label: "Compensation" },
  { journal: "other_reward", account: "other_rewards_granted", label: "Other rewards" },
  { journal: "support_grant", account: "support_grants", label: "Support grants" },
  { journal: "support_removal", account: "support_removals", label: "Support removals" },
  { journal: "opening_balance", account: "imported_pax_balances", label: "Imported balances" },
  { journal: "ai_settlement", account: "credits_used_for_ai", label: "AI usage" },
  {
    journal: "pending_creator_reward",
    account: "creator_rewards_granted",
    label: "Creator rewards",
  },
  {
    journal: "daily_expiration",
    account: "daily_credits_expired",
    label: "Daily credits expired",
  },
  {
    journal: "moderation_expiration",
    account: "moderation_credits_expired",
    label: "Moderation credits expired",
  },
  {
    journal: "refund_removal",
    account: "credits_removed_after_refunds",
    label: "Removed after refunds",
  },
  {
    journal: "failed_payment_removal",
    account: "credits_removed_after_failed_payments",
    label: "Removed after failed payments",
  },
  {
    journal: "chargeback_removal",
    account: "credits_removed_after_chargebacks",
    label: "Removed after chargebacks",
  },
  {
    journal: "closure_removal",
    account: "credits_removed_on_account_closure",
    label: "Removed on account closure",
  },
  { account: "creator_rewards_pending", label: "Creator rewards pending" },
  { journal: "creator_reward_deposit", label: "Creator reward deposits" },
  { journal: "creator_reward_forfeit", label: "Creator reward forfeits" },
  { journal: "debt_repayment", label: "Debt repayments" },
];

const accountNames = new Map(
  reportNames.flatMap(name => (name.account ? [[name.account, name.label] as const] : [])),
);
const journalNames = new Map(
  reportNames.flatMap(name => (name.journal ? [[name.journal, name.label] as const] : [])),
);

/** A system account as a person reads it: `credits_used_for_ai` is "AI usage". */
export const systemAccountLabel = (code: string) => accountNames.get(code) ?? readableCode(code);

/** A journal as the reports name it, matching its system account: `ai_settlement` is "AI usage". */
export const journalReportLabel = (code: string) => journalNames.get(code) ?? readableCode(code);

export const reportRangeLabels: Record<LedgerReportRange, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "30d": "30 days",
  month: "Month",
};

/**
 * How the summary under a chart names its window: "in the last 24 hours", "this month", or for a
 * period stepped back to, which the stepper names.
 */
export const reportRangePhrase = (range: LedgerReportRange, back = 0) =>
  back > 0
    ? "in this period"
    : range === "month"
      ? "this month"
      : `in the last ${reportRangeLabels[range]}`;

/** The balances a change touched, and what they moved by in total. */
export function bucketChange(changes: BucketAmounts) {
  const touched = Object.entries(changes) as [PlayerBucket, string][];
  return {
    buckets: touched.map(([bucket]) => bucketLabels[bucket]).join(", "),
    amountRaw: touched.reduce((total, [, amount]) => total + BigInt(amount), 0n).toString(),
  };
}

const localTime = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const localDayAndTime = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
const utcDateAndTime = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "UTC",
});

/** "1:00 PM" today, "Oct 2, 1:00 PM" on any other day, in the reader's own time. */
export function localMoment(iso: string) {
  const date = new Date(iso);
  const today = new Date().toDateString() === date.toDateString();
  return (today ? localTime : localDayAndTime).format(date);
}

export const utcMoment = (iso: string) => `${utcDateAndTime.format(new Date(iso))} UTC`;

const exactDateAndTime = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "medium",
});

/** An instant in the reader's own time, to the second: "Oct 2, 2026, 1:00:05 PM". */
export const exactMoment = (iso: string) => exactDateAndTime.format(new Date(iso));

const sinceFormat = new Intl.RelativeTimeFormat(undefined, { numeric: "always" });

/** How long before `now` an instant was: "12 seconds ago", then minutes, hours, and days. */
export function timeAgo(iso: string, now: number) {
  const seconds = Math.max(0, Math.floor((now - Date.parse(iso)) / 1000));
  if (seconds < 60) return sinceFormat.format(-seconds, "second");
  if (seconds < 3600) return sinceFormat.format(-Math.floor(seconds / 60), "minute");
  if (seconds < 86400) return sinceFormat.format(-Math.floor(seconds / 3600), "hour");
  return sinceFormat.format(-Math.floor(seconds / 86400), "day");
}

/** Text that reads as a date, as an ISO instant. A `datetime-local` value reads in the reader's time. */
export function instantFrom(value: unknown) {
  if (typeof value !== "string" || value === "") return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/** `2026-10-03T15:20:05`, an instant as a `datetime-local` input shows it in the reader's time. */
export function localInputValue(iso: string | undefined) {
  if (!iso) return "";
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}
